import { Directive, HostListener, ElementRef, Renderer2, Input } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Directive to format and validate numeric input fields.
 * Ensures the following:
 * - Only numeric values are allowed.
 * - Decimal points or commas are converted to dots.
 * - A configurable maximum number of digits after the decimal point.
 * - The value is restricted to a configurable maximum value.
 */
@Directive({
  selector: '[decimalFormatter]'
})
export class DecimalFormatterDirective {

  /**
   * The maximum value allowed for the input.
   * Default is 999999999.
   */
  @Input() maxValue: number = 999999999;
  /**
   * The Minimum value allowed for the input.
   * Default is -999999999.
   */
  @Input() minValue: number = -999999999;

  @Input() maxDigits: number = 2;

  /**
   * Constructor to inject dependencies.
   * @param el - Reference to the element the directive is applied to.
   * @param renderer - Angular's Renderer2 for DOM manipulation.
   * @param ngControl - Accessor for the form control associated with the input element.
   */
  constructor(private readonly el: ElementRef, private readonly renderer: Renderer2, private readonly ngControl: NgControl) {}

  /**
   * Listens for the 'input' event on the element and formats the value.
   * @param event - The input event.
   */
  @HostListener('input', ['$event']) onInputChange(event: Event) {
    const input = this.el.nativeElement as HTMLInputElement;
    let value = input.value;

    // Allow only digits, dot, comma, and minus sign
    value = value.replace(/[^0-9.,-]/g, '');

    // Remove all minus signs except the first one
    const firstMinusIndex = value.indexOf('-');
    if (firstMinusIndex !== -1 && firstMinusIndex !== 0) {
      // Remove all minus signs except the first one
      value = '-' + value.replace(/-/g, '');
    }

    // Ensure only one decimal point or comma is present
    const firstDotIndex = value.indexOf('.');
    const firstCommaIndex = value.indexOf(',');
    if (firstDotIndex !== -1 && firstCommaIndex !== -1) {
      // Both comma and dot found, remove comma
      value = value.replace(/,/g, '');
    } else if (firstDotIndex !== -1) {
      // Only dot found, remove additional dots
      value = value.substring(0, firstDotIndex + 1) + value.substring(firstDotIndex + 1).replace(/\./g, '');
    } else if (firstCommaIndex !== -1) {
      // Only comma found, remove additional commas and convert comma to dot
      value = value.substring(0, firstCommaIndex + 1) + value.substring(firstCommaIndex + 1).replace(/,/g, '');
      value = value.replace(/,/g, '.');
    }

    // Limit decimal to the specified number of digits
    const decimalIndex = value.indexOf('.');
    if (decimalIndex !== -1) {
      value = value.substring(0, decimalIndex + this.maxDigits + 1);
    }

    if (value === '.' || value === ',') {
      value = '';
    }

    // Limit maximum value to maxValue
    const numberValue = parseFloat(value);
    if (!isNaN(numberValue)) {
      if (numberValue > this.maxValue) {
        value = String(this.maxValue);
      } else if (numberValue < this.minValue) { // Check against the minimum value
        value = String(this.minValue);
      }
    }

    input.value = value;
    this.ngControl.control?.setValue(value, { emitEvent: false });
  }

}
